import { chunk, flatten } from 'lodash';
import pLimit from 'p-limit';
import { StoreType } from 'polotno/model/store';
import { assetBaseUrl } from '../../../config';
import { ILibraryScene, ILibraryScenePayload } from '../../../interfaces';
import { mapStorybookFilesToScenes } from '../../../mappers/library';
import { IPolotnoJSON } from '../../../mappers/polotno';
import { mapStorybookFilesResponseToStorybook } from '../../../mappers/storybook';
import { generateV4UUID } from '../../../utils/identityGenerator';
import {
    generatedImageFromServer,
    removeUnusedFontsInPage
} from '../../../utils/polotno';
import { prodBaseUrlV2 } from '../../config';
import {
    getAvailableLanguageInScene,
    getPolotnoJsonInScene,
    getSceneContentType
} from '../../utils/library';
import {
    apigClient,
    getObjectFromS3,
    listObjectsFromS3,
    uploadBase64ToS3,
    uploadToS3
} from '../aws';
import {
    IConvertedFiles,
    ISceneLayer,
    IStorybook,
    ITagsVisibilityPayload,
    LibrarySceneType,
    SceneLayerSettings
} from './../../../interfaces/index';
import {
    checkAndFixPolotnoJson,
    checkAndFixPolotnoJsonString,
    checkForMissingPolotnoPage,
    getPolotnoJson,
    getSceneLayerForPreview,
    renderLayers,
    renderMultiplePartLayers,
    TLanguage
} from './../../../mappers/polotno';
import { extractPageId, getPolotnoStore } from './../../../utils/window';

export const editorJsonBucket = 'storiez-library';
export const editorImageBucket = 'storiez-assets';
export const largeImageBucket = 'storiez-public';

export const servicesVersion = 'v2';

export interface ILibrarySearchParams {
  userId: string;
  keywords?: string;
  startIndex?: number;
  teamIds?: string[];
  activatedOnly?: boolean;
  context?: LibrarySceneType;
  excludeMine?: boolean;
}

export const getLibrarySceneFile = async <T extends boolean>({
  sceneId,
  asStorybook
}: {
  sceneId: string;
  asStorybook?: T;
}): Promise<T extends true ? IStorybook[] : ILibraryScene[]> => {
  const client = await apigClient(prodBaseUrlV2);
  const path = `/libraries/v1/scenes/uploadedFiles`;
  const body = {
    sceneId
  };
  try {
    const result = await client.invokeApi({}, path, 'POST', {}, body);
    if (asStorybook) {
      return result.data.map(mapStorybookFilesResponseToStorybook);
    } else {
      return result.data.map(mapStorybookFilesToScenes).flat();
    }
  } catch (e) {
    return [];
  }
};

export const getLibrarySceneById = async (
  id: string,
  version?: number
): Promise<ILibraryScene> => {
  const client = await apigClient(prodBaseUrlV2);
  const additionalParams = version
    ? {
        queryParams: {
          version
        }
      }
    : {};

  const path = `/libraries/${servicesVersion}/scenes/${id}`;
  const { data } = await client.invokeApi({}, path, 'GET', additionalParams);
  const editorVersion = version || data.version;

  const type = getSceneContentType(data);

  if (data.content.overlay || type !== 'layer')
    return {
      ...data,
      content: {
        ...data.content,
        overlay: checkAndFixPolotnoJsonString(data.content.overlay)
      }
    };

  try {
    try {
      const editor = await getEditorJsonAsync({
        id: data.id,
        version: editorVersion
      });
      return {
        ...data,
        content: { ...data.content, overlay: JSON.stringify(editor) }
      };
    } catch (e) {
      const editor = await getEditorJsonAsync({
        id: data.id,
        version: editorVersion - 1
      });
      return {
        ...data,
        content: { ...data.content, overlay: JSON.stringify(editor) }
      };
    }
  } catch (e) {
    return data;
  }
};

export const getActivatedSceneById = async (
  id: string
): Promise<ILibraryScene> => {
  const client = await apigClient(prodBaseUrlV2);
  const path = `/libraries/${servicesVersion}/scenes/${id}/activated`;
  const { data } = await client.invokeApi({}, path, 'GET', {});
  return data;
};

export const saveLibraryScene = async (payload: ILibraryScenePayload) => {
  const client = await apigClient(prodBaseUrlV2);
  const path = `/libraries/${servicesVersion}/scenes`;
  const result = await client.invokeApi({}, path, 'POST', {}, payload);
  return result;
};

export const deleteLibraryScene = async (id: string) => {
  const client = await apigClient(prodBaseUrlV2);
  const path = `/libraries/${servicesVersion}/scenes/${id}`;
  const result = await client.invokeApi({}, path, 'DELETE', {});
  return result;
};

export const activateScene = async (id: string, activatedBy: string) => {
  const client = await apigClient(prodBaseUrlV2);
  const path = `/libraries/${servicesVersion}/scenes/activate`;
  const result = await client.invokeApi(
    {},
    path,
    'POST',
    {},
    { id, activatedBy }
  );
  return result;
};

export const searchScene = async ({
  userId,
  keywords,
  startIndex,
  activatedOnly,
  teamIds,
  excludeMine,
  context
}: ILibrarySearchParams): Promise<ILibraryScene[]> => {
  const client = await apigClient(prodBaseUrlV2);

  const contextQuery =
    context === 'template' ? `type: 'template'` : `(not type: 'template')`;

  const hasTeam = teamIds?.length > 0;
  const teamsQuery = hasTeam
    ? ` (and visibility: 'team' (or ${teamIds
        .map((id) => `visibility_scope: '${id}'`)
        .join(' ')}))`
    : ` (and visibility: 'team' visibility_scope: 'none')`;

  const filterInvisible = `(not visibility: 'hide')`;
  const queryString = excludeMine
    ? `(and (not createdby: '${userId}')${teamsQuery})`
    : hasTeam
    ? `(or createdby: '${userId}'${teamsQuery})`
    : `createdby: '${userId}'`;

  const body = {
    keywords: keywords || '',
    size: 10,
    startIndex: startIndex || 0,
    queryString: `(and ${contextQuery} ${filterInvisible} ${queryString})`
  };

  const additionalParams = activatedOnly
    ? {
        queryParams: {
          activated: true
        }
      }
    : {};

  const path = `/libraries/${servicesVersion}/scenes/search`;
  const result = await client.invokeApi(
    {},
    path,
    'POST',
    additionalParams,
    body
  );
  return result.data.scenes;
};

export const updateSceneAttributes = async (body: ITagsVisibilityPayload) => {
  const client = await apigClient(prodBaseUrlV2);
  const path = `/libraries/${servicesVersion}/scenes/attributes`;
  return await client.invokeApi({}, path, 'POST', {}, body);
};

export const getEditorJson = ({
  id,
  version,
  onSuccess,
  onError
}: {
  id: string;
  version: number;
  onSuccess: (json: IPolotnoJSON) => void;
  onError?: () => void;
}) => {
  getObjectFromS3(editorJsonBucket, `editor/${id}/v${version}.json`)
    .then((data) => {
      onSuccess(JSON.parse(data.Body.toString()));
    })
    .catch(() => {
      onError?.();
    });
};

export const getEditorJsonAsync = async ({
  id,
  version
}: {
  id: string;
  version: number;
}): Promise<IPolotnoJSON> => {
  const data = await getObjectFromS3(
    editorJsonBucket,
    `editor/${id}/v${version}.json`
  );
  return checkAndFixPolotnoJson(JSON.parse(data.Body.toString()));
};

export const saveEditorJson = ({
  id,
  version,
  json,
  onSuccess,
  onError
}: {
  id: string;
  version: number;
  json: IPolotnoJSON;
  onSuccess?: () => void;
  onError?: () => void;
}) => {
  uploadToS3({
    Body: Buffer.from(JSON.stringify(json)),
    Bucket: editorJsonBucket,
    Key: `editor/${id}/v${version || '1'}.json`,
    ContentType: 'application/json'
  })
    .then(() => onSuccess?.())
    .catch(() => onError?.());
};

type SceneGeneratedPage = {
  name: string;
  pixelRatio?: number;
  pageId?: string;
  isPortrait?: boolean;
  language?: TLanguage;
};
export class RenderLayerQueue {
  size = 0;
  queueLayers = [];
  renderedResponse: any;
  constructor(size: number) {
    this.size = size;
  }

  async add(input, onComplete) {
    const layerIndex = this.queueLayers.length;

    this.queueLayers.push(input);

    if (this.queueLayers.length === this.size) {
      this.renderedResponse = await renderMultiplePartLayers(this.queueLayers);
    }
    let timer = setInterval(() => {
      if (this.renderedResponse?.mapped?.[layerIndex]) {
        onComplete(this.renderedResponse.mapped[layerIndex]);
        clearInterval(timer);
        timer = null;
      }
    });
    setTimeout(() => {
      if (timer) {
  
        clearInterval(timer);
      }
    }, 60000);
  }
}
const uploadRenderedLayers = async (
  layers: any[],
  store: StoreType,
  keyBase: string,
  renderQueue?: RenderLayerQueue
): Promise<SceneLayerSettings[]> => {
  try {
    let mapped;
    if (renderQueue) {
      mapped = await new Promise((resolve, reject) => {
        renderQueue.add({ layers, store, keyBase }, (rs) => {
          if (rs) {
            resolve(rs);
          } else {
            reject();
          }
        });
      });
    } else {
      mapped = (await renderLayers(layers, store, keyBase)).mapped;
    }

    const fileExtension = (transparent: boolean) =>
      transparent ? 'png' : 'jpeg';
    const fileName = (i: number, transparent: boolean) =>
      `${keyBase}layer_${i + 1}.${fileExtension(transparent)}`;
    return mapped.map((layer, index) => ({
      ...layer,
      url:
        layer.type === 'video' || layer.type === 'gif'
          ? layer.url
          : `${assetBaseUrl}/${fileName(index, layer.transparent)}`
    }));
  } catch (e) {
    return undefined;
  }
};

export const savePageLayers = async (
  store: StoreType,
  keyBase: string,
  scene?: ILibraryScene,
  isNewScene?: boolean,
  upload = true
): Promise<ISceneLayer> => {
  let layers = {
    desktop: undefined,
    mobile: undefined
  };

  // Use live store data only, like Show Preview does - don't pass scene to avoid using stale overlay data
  layers = getSceneLayerForPreview(store?.toJSON()) || {
    desktop: undefined,
    mobile: undefined
  };

  if (!layers.desktop) return;
  try {
    const renderedLayers: ISceneLayer = {};
    const renderQueue = new RenderLayerQueue(
      Object.keys(layers).filter((key) => {
        return upload && layers[key].needGenerateLayer && layers[key].value;
      }).length
    );

    const results = await Promise.all(
      Object.keys(layers).map((key) => {
        const rendered =
          upload && layers[key].needGenerateLayer && layers[key].value
            ? uploadRenderedLayers(
                layers[key].value,
                store,
                `${keyBase}/${key}_`,
                renderQueue
              )
            : layers[key].value;
        return rendered;
      })
    );

    results.map((result, index) => {
      renderedLayers[Object.keys(layers)[index]] = result;
    });

    return renderedLayers;
  } catch (e) {
    return e;
  }
};

export interface PolotnoGeneratedImages {
  mainImage?: string;
  thumbnail?: string;
  preview?: string;
  portraitImage?: string;
  portraitThumbnail?: string;
  portraitPreview?: string;
  layer?: ISceneLayer;
  imageLanguages?: TLanguage[];
}

export const mapLanguageToImageName = (name: string, language: TLanguage) => {
  return `${name}${language === 'en' ? '' : `-${language}`}`;
};

export const saveSceneImage = async ({
  key,
  initStore,
  scene,
  isNewScene,
  onProgress
}: {
  key: string;
  initStore?: StoreType;
  scene?: ILibraryScene;
  isNewScene?: boolean;
  onProgress?: (progress: number, message: string) => void;
}): Promise<PolotnoGeneratedImages> => {
  const store = initStore || getPolotnoStore();
  const generatedKey = generateV4UUID();
  const scenePages = getPolotnoJsonInScene(scene)?.pages;

  // Validate store availability
  if (!store) {
    console.error('saveSceneImage - Polotno store not available');
    return {
      layer: {},
      imageLanguages: ['en']
    };
  }

  // Ensure the rendering store is available
  if (!window['polotnoRenderingStore']) {
    console.warn('Polotno rendering store not found, attempting to initialize...');
    try {
      const { getRenderingStore } = await import('../../../advisorHub/components/Polotno/polotnoInit');
      const renderingStore = getRenderingStore();
    
    } catch (error) {
      console.error('Failed to initialize rendering store:', error);
      // Return early if we can't initialize the rendering store
      return {
        layer: {},
        imageLanguages: ['en']
      };
    }
  }
  
  // Double-check that the rendering store is now available
  if (!window['polotnoRenderingStore']) {
    console.error('Polotno rendering store still not available after initialization attempt');
    return {
      layer: {},
      imageLanguages: ['en']
    };
  }

  const keyBase = `content/generated/${key}/${generatedKey}`;
  const highResolutionKeyBase = `scenes/${key.split('/')[0]}`;

  const languages = getAvailableLanguageInScene(scene);


  onProgress?.(5, 'Analyzing scene structure...');

  // Validate that we have valid scene content
  if (!scenePages || scenePages.length === 0) {
    console.error('saveSceneImage - No valid scene pages found:', {
      sceneId: scene?.id,
      hasOverlay: !!scene?.content?.overlay,
      hasEditor: !!scene?.editor
    });
    return {
      layer: {},
      imageLanguages: languages || ['en']
    };
  }

  const landscapeImage: SceneGeneratedPage[] = [];
  const sceneImages: SceneGeneratedPage[] = [];
  const portraitImage: SceneGeneratedPage[] = [];

  languages.map((l) => {
    sceneImages.push(
      {
        name: `${keyBase}/${mapLanguageToImageName('scene', l)}.jpeg`,
        language: l
      },
      {
        name: `${keyBase}/${mapLanguageToImageName('scene_portrait', l)}.jpeg`,
        language: l,
        isPortrait: true
      }
    );

    landscapeImage.push(
      {
        name: `${keyBase}/${mapLanguageToImageName('scene_preview', l)}.jpeg`,
        pixelRatio: 0.5,
        language: l
      },
      {
        name: `${highResolutionKeyBase}/${mapLanguageToImageName(
          'sceneImage_landscape',
          l
        )}.jpeg`,
        pixelRatio: 1.2,
        language: l
      },
      {
        name: `${keyBase}/${mapLanguageToImageName('scene_thumbnail', l)}.jpeg`,
        pixelRatio: 0.5,
        language: l
      }
    );

    portraitImage.push(
      {
        name: `${keyBase}/${mapLanguageToImageName(
          'scene_preview_portrait',
          l
        )}.jpeg`,
        pixelRatio: 0.5,
        language: l,
        isPortrait: true
      },
      {
        name: `${highResolutionKeyBase}/${mapLanguageToImageName(
          'sceneImage_portrait',
          l
        )}.jpeg`,
        pixelRatio: 1.2,
        language: l,
        isPortrait: true
      },
      {
        name: `${keyBase}/${mapLanguageToImageName(
          'scene_thumbnail_portrait',
          l
        )}.jpeg`,
        pixelRatio: 0.5,
        language: l,
        isPortrait: true
      }
    );
  });



  try {

    // Generate all images (scene, preview, thumbnail, high-res) and get data URLs
    const allImagesToGenerate = [
      ...sceneImages,
      ...landscapeImage,
      ...portraitImage
    ];



    const allImageLayers = allImagesToGenerate.map((image) => {

      
      const languagePages = scenePages.filter((p) =>
        p.id.includes(`${image.language === 'en' ? '' : `-${image.language}`}`)
      );

      
      const page = languagePages.find((p) =>
        image.isPortrait
          ? (p.id.includes('-portrait') || p.id.startsWith('portrait-'))
          : (!p.id.includes('-portrait') && !p.id.startsWith('portrait-'))
      );

      if (!page) {
        console.warn(`❌ No page found for ${image.name} (isPortrait: ${image.isPortrait})`);
        return null;
      }



      return {
        json: removeUnusedFontsInPage({
          ...store.toJSON(),
          pages: [page]
        }),
        exportOptions: {
          pixelRatio: image.pixelRatio || 1,
          mimeType: 'image/jpeg'
        },
        // Don't upload to S3 yet - we'll do it after creating all versions
        uploadOptions: undefined,
        language: image.language,
        isPortrait: image.isPortrait
      };
    });

    const validLayers = allImageLayers.filter(layer => layer !== null);
    
    // Reduce chunk size and concurrency for better stability
    const layerChunks = chunk(validLayers, 1); // Process one image at a time per chunk
    const limit = pLimit(1); // Reduce to single concurrent operation to prevent memory issues
    

    
    onProgress?.(10, `Preparing to generate ${validLayers.length} images across ${languages.length} language(s)...`);

    // Add timeout wrapper for the entire image generation process
    const generateImagesWithTimeout = async () => {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('Image generation process timed out after 5 minutes'));
        }, 300000); // 5 minute total timeout
      });

      const imageGenerationPromise = Promise.all(
        layerChunks.map(async (chunk, index) => {
          try {
            const chunkNum = index + 1;
            const totalChunks = layerChunks.length;
            
            // Calculate progress: 10% start + 50% for image generation (distributed across chunks)
            const baseProgress = 10;
            const imageGenerationProgress = 50;
            const chunkProgress = baseProgress + (chunkNum - 1) * (imageGenerationProgress / totalChunks);
            
            // Determine what type of images we're processing
            const imageTypes = chunk.map(c => {
              if (c.isPortrait) return 'portrait';
              return 'landscape';
            }).join(', ');
            
            const languageInfo = chunk.map(c => c.language).filter((l, i, arr) => arr.indexOf(l) === i).join(', ');
            
    
            onProgress?.(Math.round(chunkProgress), `Generating ${imageTypes} images (${languageInfo})...`);
            
            const result = await limit(() => generatedImageFromServer(chunk));
            
            const completedProgress = baseProgress + chunkNum * (imageGenerationProgress / totalChunks);
    
            onProgress?.(Math.round(completedProgress), `Completed ${chunkNum}/${totalChunks} image batches`);
            
            return result;
          } catch (error) {
            console.error(`saveSceneImage - Error processing chunk ${index + 1}:`, error);
            // Return empty array for failed chunks to maintain array structure
            return [];
          }
        })
      );

      return Promise.race([imageGenerationPromise, timeoutPromise]);
    };

    const allImageDataUrls = await generateImagesWithTimeout() as string[][];
    
    // Flatten the results and create a map of image name to data URL
    const flatAllImageDataUrls = allImageDataUrls.flat();
    
    // Generate layers using the current store state
    onProgress?.(65, 'Generating scene layers...');

    let generatedLayers = {};
    try {
      generatedLayers = await savePageLayers(
        store,
        keyBase,
        scene,
        isNewScene
      );
  
      onProgress?.(75, 'Scene layers generated successfully');
    } catch (error) {
      console.error('Error generating layers:', error);
      onProgress?.(75, 'Layer generation completed (some layers may have failed)');
      // Continue with empty layers if layer generation fails
    }
    
    const imageDataUrlMap = {};
    // Create a more robust mapping by matching the allImageLayers with allImagesToGenerate
    let dataUrlIndex = 0;
    allImageLayers.forEach((layer, layerIndex) => {
      if (layer && flatAllImageDataUrls[dataUrlIndex]) {
        const image = allImagesToGenerate[layerIndex];
        if (image) {
          imageDataUrlMap[image.name] = flatAllImageDataUrls[dataUrlIndex];

        }
        dataUrlIndex++;
      }
    });
    
    // Upload all generated images to S3
    onProgress?.(80, `Uploading ${allImagesToGenerate.length} images to storage...`);
    const uploadPromises = allImagesToGenerate.map(async (image) => {
      const dataUrl = imageDataUrlMap[image.name];
      if (dataUrl) {
        try {
          const bucket = image.pixelRatio === 1.2 ? largeImageBucket : editorImageBucket;
          await uploadBase64ToS3({
            Body: dataUrl,
            Bucket: bucket,
            Key: image.name,
            ContentType: 'image/jpeg'
          });

        } catch (error) {
          console.error(`Failed to upload ${image.name}:`, error);
          // Don't throw here, continue with other uploads
        }
      } else {
        console.warn(`No data URL found for ${image.name}`);
      }
    });
    
    // Wait for all uploads to complete, but don't fail if some uploads fail
    try {
      await Promise.allSettled(uploadPromises);
  
      onProgress?.(90, 'Image uploads completed successfully');
    } catch (error) {
      console.error('Error during upload phase:', error);
      onProgress?.(90, 'Upload completed (some uploads may have failed)');
      // Continue with the process even if uploads fail
    }

    // Layers were already generated above, use the existing generatedLayers
    onProgress?.(95, 'Finalizing scene assets...');
    const result = {
      layer: generatedLayers || {},
      imageLanguages: languages
    };

    [...sceneImages.filter((p) => !p.isPortrait), ...landscapeImage].map(
      (image) => {
        const imageLanguage = image.language;
        const pages = scenePages.filter((p) => {
          const { language } = extractPageId(p.id);
          return language === imageLanguage;
        });
        const hasPortrait =
          checkForMissingPolotnoPage({ pages, width: 0, height: 0 }) !==
          'mobile';
        result[
          `mainImage${imageLanguage === 'en' ? '' : `-${imageLanguage}`}`
        ] = `${assetBaseUrl}/${image.name}`;
        result[
          `thumbnail${imageLanguage === 'en' ? '' : `-${imageLanguage}`}`
        ] = `${assetBaseUrl}/${image.name}`;
        result[
          `preview${imageLanguage === 'en' ? '' : `-${imageLanguage}`}`
        ] = `${assetBaseUrl}/${image.name}`;

        const portraitUrl =
          imageLanguage === 'en'
            ? image.name.replace('.jpeg', '_portrait.jpeg')
            : image.name.replace(
                `-${imageLanguage}.jpeg`,
                `_portrait-${imageLanguage}.jpeg`
              );
        result[
          `portraitImage${imageLanguage === 'en' ? '' : `-${imageLanguage}`}`
        ] = `${assetBaseUrl}/${!hasPortrait ? image.name : portraitUrl}`;
        result[
          `portraitThumbnail${
            imageLanguage === 'en' ? '' : `-${imageLanguage}`
          }`
        ] = `${assetBaseUrl}/${!hasPortrait ? image.name : portraitUrl}`;
        result[
          `portraitPreview${imageLanguage === 'en' ? '' : `-${imageLanguage}`}`
        ] = `${assetBaseUrl}/${!hasPortrait ? image.name : portraitUrl}`;
      }
    );
    onProgress?.(100, 'Scene image generation completed');
    return result;
  } catch (e) {
    console.error('Error in saveSceneImage:', e);
    // Return a minimal result to prevent activation from failing completely
          return {
        layer: {},
        imageLanguages: languages || ['en']
      };
  }
};

export const saveEditorImage = async ({
  key,
  initStore,
  scene,
  isNewScene
}: {
  key: string;
  initStore?: StoreType;
  scene?: ILibraryScene;
  isNewScene?: boolean;
}): Promise<PolotnoGeneratedImages> => {
  const store = initStore || getPolotnoStore();
  const generatedKey = generateV4UUID();

  const keyBase = `content/generated/${key}/${generatedKey}`;
  const highResolutionKeyBase = `scenes/${key.split('/')[0]}`;

  const hasPortrait =
    checkForMissingPolotnoPage(getPolotnoJson().obj) !== 'mobile';

  const { language } = extractPageId(store.pages[0]?.id);
  const typedLanguage = language as TLanguage;

  const landscapeImage: SceneGeneratedPage[] = [
    {
      name: `${keyBase}/${mapLanguageToImageName('scene', typedLanguage)}.jpeg`
    },
    {
      name: `${highResolutionKeyBase}/${mapLanguageToImageName(
        'sceneImage_landscape',
        typedLanguage
      )}.jpeg`,
      pixelRatio: 1.2
    },
    {
      name: `${keyBase}/${mapLanguageToImageName(
        'scene_preview',
        typedLanguage
      )}.jpeg`,
      pixelRatio: 0.5
    },
    {
      name: `${keyBase}/${mapLanguageToImageName(
        'scene_thumbnail',
        typedLanguage
      )}.jpeg`,
      pixelRatio: 0.5
    }
  ];

  const portraitImage: SceneGeneratedPage[] = [
    {
      name: `${keyBase}/${mapLanguageToImageName(
        'scene_portrait',
        typedLanguage
      )}.jpeg`
    },
    {
      name: `${highResolutionKeyBase}/${mapLanguageToImageName(
        'sceneImage_portrait',
        typedLanguage
      )}.jpeg`,
      pixelRatio: 1.2
    },
    {
      name: `${keyBase}/${mapLanguageToImageName(
        'scene_preview_portrait',
        typedLanguage
      )}.jpeg`,
      pixelRatio: 0.5
    },
    {
      name: `${keyBase}/${mapLanguageToImageName(
        'scene_thumbnail_portrait',
        typedLanguage
      )}.jpeg`,
      pixelRatio: 0.5
    }
  ];

  const allImages = hasPortrait
    ? [
        ...landscapeImage,
        ...portraitImage.map((p) => ({ ...p, isPortrait: true }))
      ]
    : landscapeImage;

  try {
    const _generatedImages = await Promise.all(
      allImages.map((image) =>
        generatedImageFromServer([
          {
            json: removeUnusedFontsInPage({
              ...store.toJSON(),
              pages: [store.toJSON().pages[image.isPortrait ? '1' : '0']]
            }),
            exportOptions: {
              pixelRatio: image.pixelRatio,
              mimeType: 'image/jpeg'
            },
            uploadOptions: {
              bucket: editorImageBucket,
              Key: image.name,
              ContentType: 'image/jpeg'
            }
          }
        ])
      )
    );

    const generatedLayers = await savePageLayers(
      store,
      keyBase,
      scene,
      isNewScene
    );
    const [mainImageName, previewName, thumbnailName] = landscapeImage.map(
      (image) => image.name
    );

    const [portraitImageName, portraitPreviewName, portraitThumbnailName] =
      portraitImage.map((image) => image.name);

    const result = {
      layer: generatedLayers
    };
    result[`imageLanguage`] = typedLanguage;
    result[
      `mainImage${typedLanguage === 'en' ? '' : `-${typedLanguage}`}`
    ] = `${assetBaseUrl}/${mainImageName}`;
    result[
      `thumbnail${typedLanguage === 'en' ? '' : `-${typedLanguage}`}`
    ] = `${assetBaseUrl}/${thumbnailName}`;
    result[
      `preview${typedLanguage === 'en' ? '' : `-${typedLanguage}`}`
    ] = `${assetBaseUrl}/${previewName}`;
    result[
      `portraitImage${typedLanguage === 'en' ? '' : `-${typedLanguage}`}`
    ] = `${assetBaseUrl}/${hasPortrait ? portraitImageName : mainImageName}`;
    result[
      `portraitThumbnail${typedLanguage === 'en' ? '' : `-${typedLanguage}`}`
    ] = `${assetBaseUrl}/${
      hasPortrait ? portraitThumbnailName : thumbnailName
    }`;
    result[
      `portraitPreview${typedLanguage === 'en' ? '' : `-${typedLanguage}`}`
    ] = `${assetBaseUrl}/${hasPortrait ? portraitPreviewName : previewName}`;
    return result;
  } catch (e) {
    return {};
  }
};

export const getUploadedFileConvertingStatus = async (
  uploadId
): Promise<IConvertedFiles[]> => {
  const client = await apigClient(prodBaseUrlV2);
  const path = `/libraries/v1/files/uploadedFiles`;
  const body = {
    uploadId
  };
  try {
    const result = await client.invokeApi({}, path, 'POST', {}, body);
    return result?.data;
  } catch (e) {
    return [];
  }
};

export const getEditorShapeListFromS3 = async () => {
  const payload = {
    Bucket: editorImageBucket,
    Prefix: 'editor/shape/',
    Delimiter: '/'
  };

  const { CommonPrefixes } = await listObjectsFromS3(payload);

  const data = await Promise.all(
    CommonPrefixes.map(async (prefix) => {
      const { Contents } = await listObjectsFromS3({
        Bucket: editorImageBucket,
        Prefix: prefix.Prefix
      });
      const shape = Contents.map((content) => ({
        name: content.Key.split('/')[2],
        url: `${assetBaseUrl}/${content.Key}`
      }));
      return shape;
    })
  );

  return flatten(data);
};

export const transferSceneOwnership = async (
  scene: ILibraryScene,
  newOwnerId: string
) => {
  const url =
    'https://1767u9oiwk.execute-api.ap-southeast-1.amazonaws.com/prod';
  const client = await apigClient(url);
  const path = `/libraries/v1/scenes/attributes`;
  const body = {
    id: scene.id,
    tags: scene.tags,
    visibility: scene.content.visibility,
    visibilityScope: scene.content.visibilityScope,
    createdBy: newOwnerId
  };
  return await client.invokeApi({}, path, 'POST', {}, body);
};
